import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
    Select, Row, Col, Spin, Space, Empty, Table, Tag, Progress, Tooltip,
} from "antd";
import {
    WarningOutlined, SearchOutlined, CalendarOutlined,
} from "@ant-design/icons";
import { Input } from "antd";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;
const { Search } = Input;

export default function LeaveUtilization() {
    const { authData, backUrl } = useContext(AuthContext);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [organisations, setOrganisations] = useState([]);
    const [organisationId, setOrganisationId] = useState("ALL");
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setOrganisations(res.data.results || res.data || []))
          .catch(() => {});
    }, [backUrl, authData?.access]);

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (organisationId !== "ALL") params.organisation_id = organisationId;

        axios.get(`${backUrl}/api/reports/leave-utilization/`, {
            params,
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setData(Array.isArray(res.data) ? res.data : []))
          .catch(() => setData([]))
          .finally(() => setLoading(false));
    }, [organisationId, backUrl, authData?.access]);

    const filteredData = data.filter(emp =>
        (emp.employee_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.employee_code || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.department || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    /* Summary counts */
    const lowClBal   = data.filter(e => (e.cl_balance ?? 0) <= 2).length;
    const lowSlBal   = data.filter(e => (e.sl_balance ?? 0) <= 2).length;
    const totalUsed  = data.reduce((s, e) => s + (e.total_used ?? ((e.cl_used || 0) + (e.sl_used || 0))), 0);

    /* Helper: balance color */
    const balColor = (bal) => {
        if (bal === undefined || bal === null) return "#64748b";
        if (bal <= 1) return "#dc2626";
        if (bal <= 2) return "#f59e0b";
        return "#10b981";
    };

    const columns = [
        {
            title: "Code",
            dataIndex: "employee_code",
            width: 80,
            render: v => (
                <Tag style={{ fontWeight: 700, borderRadius: 6, margin: 0, fontSize: 11 }}>{v || "-"}</Tag>
            ),
        },
        {
            title: "Employee",
            dataIndex: "employee_name",
            ellipsis: true,
            render: (v, r) => (
                <div>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 13 }}>{v}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.department || "-"}</div>
                </div>
            ),
        },
        {
            title: "CL Used / Total",
            key: "cl",
            width: 140,
            render: (_, r) => {
                const used  = r.cl_used  ?? 0;
                const total = r.cl_total ?? 12;
                const pct   = Math.min(Math.round((used / Math.max(total, 1)) * 100), 100);
                return (
                    <div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#334155" }}>
                                {used} / {total} days
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: pct >= 80 ? "#ef4444" : "#64748b" }}>
                                {pct}%
                            </span>
                        </div>
                        <Progress
                            percent={pct}
                            showInfo={false}
                            strokeColor={pct >= 80 ? "#ef4444" : pct >= 50 ? "#f59e0b" : "#10b981"}
                            trailColor="#e2e8f0"
                            strokeWidth={6}
                            style={{ margin: 0 }}
                        />
                    </div>
                );
            },
        },
        {
            title: "SL Used / Total",
            key: "sl",
            width: 140,
            render: (_, r) => {
                const used  = r.sl_used  ?? 0;
                const total = r.sl_total ?? 12;
                const pct   = Math.min(Math.round((used / Math.max(total, 1)) * 100), 100);
                return (
                    <div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#334155" }}>
                                {used} / {total} days
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: pct >= 80 ? "#ef4444" : "#64748b" }}>
                                {pct}%
                            </span>
                        </div>
                        <Progress
                            percent={pct}
                            showInfo={false}
                            strokeColor={pct >= 80 ? "#ef4444" : pct >= 50 ? "#f59e0b" : "#3b82f6"}
                            trailColor="#e2e8f0"
                            strokeWidth={6}
                            style={{ margin: 0 }}
                        />
                    </div>
                );
            },
        },
        {
            title: "CL Balance",
            dataIndex: "cl_balance",
            width: 90,
            align: "center",
            render: v => {
                const color = balColor(v);
                return (
                    <Tooltip title={v <= 2 ? "Low CL balance — employee may exhaust leave soon" : undefined}>
                        <Tag style={{
                            fontWeight: 800, borderRadius: 8, margin: 0,
                            color, background: `${color}18`, border: `1px solid ${color}40`,
                            fontSize: 12,
                        }}>
                            {v ?? 0}
                            {v <= 2 && v !== undefined && (
                                <WarningOutlined style={{ marginLeft: 4, fontSize: 10 }} />
                            )}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: "SL Balance",
            dataIndex: "sl_balance",
            width: 90,
            align: "center",
            render: v => {
                const color = balColor(v);
                return (
                    <Tooltip title={v <= 2 ? "Low SL balance — employee may exhaust leave soon" : undefined}>
                        <Tag style={{
                            fontWeight: 800, borderRadius: 8, margin: 0,
                            color, background: `${color}18`, border: `1px solid ${color}40`,
                            fontSize: 12,
                        }}>
                            {v ?? 0}
                            {v <= 2 && v !== undefined && (
                                <WarningOutlined style={{ marginLeft: 4, fontSize: 10 }} />
                            )}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: "Total Used",
            key: "total_used",
            width: 100,
            align: "center",
            render: (_, r) => {
                const total = r.total_used ?? ((r.cl_used || 0) + (r.sl_used || 0));
                return (
                    <Tag style={{
                        fontWeight: 800, borderRadius: 8, margin: 0,
                        background: "#e0f2fe", color: "#0284c7", border: "1px solid #7dd3fc",
                    }}>
                        {total} days
                    </Tag>
                );
            },
        },
    ];

    return (
        <div style={{
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            borderRadius: 16, border: "1px solid #e2e8f0",
            boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
            overflow: "hidden", height: "100%",
        }}>
            <div style={{ height: 3.5, background: "linear-gradient(90deg, #0e497b, #3b82f6)" }} />
            <div style={{ padding: "16px 20px" }}>
                {/* Header */}
                <Row justify="space-between" align="middle" gutter={[10, 10]} style={{ marginBottom: 14 }}>
                    <Col>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <CalendarOutlined style={{ color: "#0e497b", fontSize: 18 }} />
                            <div>
                                <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>
                                    Leave Utilization Tracker
                                </div>
                                <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                                    CL &amp; SL balance, usage ratios and alerts
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col>
                        <Space size={6} wrap>
                            <Search
                                placeholder="Search Employee / Code..."
                                onSearch={setSearchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{ width: 180 }}
                                allowClear
                                size="middle"
                                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                            />
                            <Select value={organisationId} onChange={setOrganisationId} style={{ width: 175 }} size="middle">
                                <Option value="ALL">All Organisations ({organisations.length})</Option>
                                {organisations.map(o => <Option key={o.id} value={o.id}>{o.name}</Option>)}
                            </Select>
                        </Space>
                    </Col>
                </Row>

                {/* Summary Strip */}
                {!loading && data.length > 0 && (
                    <Row gutter={[10, 8]} style={{ marginBottom: 14 }}>
                        {[
                            { label: "Total Employees", val: data.length, color: "#0e497b", bg: "#eff6ff", border: "#bfdbfe" },
                            { label: "Total Leave Used", val: `${totalUsed} days`, color: "#7c3aed", bg: "#faf5ff", border: "#ddd6fe" },
                            { label: "Low CL Balance", val: lowClBal, color: lowClBal > 0 ? "#f59e0b" : "#10b981", bg: lowClBal > 0 ? "#fffbeb" : "#f0fdf4", border: lowClBal > 0 ? "#fde68a" : "#bbf7d0" },
                            { label: "Low SL Balance", val: lowSlBal, color: lowSlBal > 0 ? "#ef4444" : "#10b981", bg: lowSlBal > 0 ? "#fff1f2" : "#f0fdf4", border: lowSlBal > 0 ? "#fecaca" : "#bbf7d0" },
                        ].map(k => (
                            <Col xs={12} sm={6} key={k.label}>
                                <div style={{
                                    background: k.bg, border: `1px solid ${k.border}`,
                                    borderRadius: 10, padding: "7px 10px", textAlign: "center",
                                }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: k.color, textTransform: "uppercase", marginBottom: 2 }}>
                                        {k.label}
                                    </div>
                                    <div style={{ fontSize: 15, fontWeight: 900, color: k.color }}>{k.val}</div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                )}

                {loading ? (
                    <div style={{ textAlign: "center", padding: "60px 0" }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>
                            Loading Leave Utilization Data...
                        </div>
                    </div>
                ) : filteredData.length === 0 ? (
                    <Empty description="No leave balance records found" style={{ padding: "50px 0" }} />
                ) : (
                    <Table
                        dataSource={filteredData}
                        columns={columns}
                        rowKey={r => r.employee_id || r.employee_code}
                        pagination={{ pageSize: 6, showSizeChanger: false }}
                        size="small"
                        scroll={{ x: 820 }}
                        rowClassName={(_, idx) => idx % 2 === 0 ? "" : ""}
                        onRow={(rec) => ({
                            style: {
                                background: (rec.cl_balance <= 1 || rec.sl_balance <= 1)
                                    ? "#fff7f7"
                                    : (rec.cl_balance <= 2 || rec.sl_balance <= 2)
                                    ? "#fffdf0"
                                    : "#ffffff",
                            },
                        })}
                    />
                )}
            </div>
        </div>
    );
}
